import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

// ============ BLACKLIST VIEWER (restore permanently-deleted codes) ============
// Supports two views:
//   • "Son sync'te geldi" → only entries Wolvox tried to re-send
//     (pending_restore=true). The operator decides restock OR keep ignored.
//   • "Tümü" → full blacklist for admin housekeeping.
export default function BlacklistDialog({ open, onOpenChange, onRestored }) {
  const [data, setData] = useState({ total: 0, pending_total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [mode, setMode] = useState("pending"); // "pending" | "all"

  const refresh = async () => {
    setLoading(true);
    try {
      const url = `/admin/products/blacklist?limit=1000${mode === "pending" ? "&only_pending=true" : ""}`;
      const r = await api.get(url);
      setData(r.data || { total: 0, pending_total: 0, items: [] });
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) refresh(); }, [open, mode]);

  // When the dialog opens, default to pending if there are any pending items,
  // otherwise show the full list so empty-state is friendlier.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get("/admin/products/blacklist/pending-count");
        setMode((r.data?.pending || 0) > 0 ? "pending" : "all");
      } catch {}
    })();
  }, [open]);

  const toggle = (code) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(code)) n.delete(code); else n.add(code);
      return n;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      const allSelected = data.items.every((it) => n.has(it.akinsoft_code));
      if (allSelected) data.items.forEach((it) => n.delete(it.akinsoft_code));
      else data.items.forEach((it) => n.add(it.akinsoft_code));
      return n;
    });
  };

  const restore = async () => {
    const codes = Array.from(selected);
    if (!codes.length) return;
    if (!window.confirm(`${codes.length} ürün kara listeden çıkarılacak. Bir sonraki Wolvox sync'inde web'e geri gelecek. Devam?`)) return;
    try {
      await api.post("/admin/products/blacklist/restore", { codes });
      toast.success(`✓ ${codes.length} ürün geri alındı — sonraki sync'te web'e eklenecek`);
      await refresh();
      onRestored?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geri alınamadı");
    }
  };

  const dismiss = async () => {
    const codes = Array.from(selected);
    if (!codes.length) return;
    if (!window.confirm(`${codes.length} ürün için "silinmiş kalsın" seçiliyor. Bir daha uyarı gelmeyecek (ta ki Wolvox tekrar göndermeye çalışana kadar). Devam?`)) return;
    try {
      const { data: res } = await api.post("/admin/products/blacklist/dismiss", { codes });
      toast.success(`✓ ${res.dismissed} ürün yoksayıldı`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yoksayılamadı");
    }
  };

  const allVisibleSelected = data.items.length > 0 && data.items.every((it) => selected.has(it.akinsoft_code));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="blacklist-dialog" className="bg-[#FAF7F2] border-[#E8E2D5] max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            🚫 Silinmiş Ürün Kara Listesi
          </DialogTitle>
          <DialogDescription>
            Bir ürünü seç → <b>"Geri Al"</b> dersen bir sonraki sync'te web'e tekrar gelir.
            <b> "Yoksay"</b> dersen "silinmiş kalsın" — ta ki Wolvox tekrar göndermeye çalışana kadar.
          </DialogDescription>
        </DialogHeader>

        {/* Mode switcher */}
        <div className="flex gap-2 mb-2">
          <Button
            data-testid="blacklist-tab-pending"
            size="sm"
            variant={mode === "pending" ? "default" : "outline"}
            onClick={() => setMode("pending")}
            className={`rounded-full h-9 ${mode === "pending" ? "bg-[#D9653B] hover:bg-[#B4522F] text-white" : "border-[#D9653B] text-[#D9653B] hover:bg-[#D9653B]/10"}`}
          >
            ⚠️ Son sync'te geldi ({data.pending_total})
          </Button>
          <Button
            data-testid="blacklist-tab-all"
            size="sm"
            variant={mode === "all" ? "default" : "outline"}
            onClick={() => setMode("all")}
            className={`rounded-full h-9 ${mode === "all" ? "bg-[#1B2A22] text-white" : "border-[#1B2A22] text-[#1B2A22]/70"}`}
          >
            Tümü
          </Button>
        </div>

        {mode === "pending" && data.pending_total > 0 && (
          <div className="bg-[#D9653B]/10 border border-[#D9653B]/40 rounded-xl p-3 text-xs text-[#B4522F]">
            🔔 <b>Wolvox bu ürünleri tekrar göndermeye çalıştı</b> (sen daha önce silmiştin).
            Stoklara geri yüklediysen <b>"Geri Al"</b> → sync web'e ekler.
            Hâlâ silmek istiyorsan <b>"Yoksay"</b> → uyarı gider.
          </div>
        )}

        {loading ? (
          <div className="text-center py-6 text-[#1B2A22]/60">Yükleniyor...</div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-10 text-[#1B2A22]/60">
            {mode === "pending" ? "✓ Dikkat gereken ürün yok." : "Kara liste boş."}
          </div>
        ) : (
          <>
            {selected.size > 0 && (
              <div className="flex gap-2 flex-wrap">
                <Button
                  data-testid="blacklist-restore-btn"
                  onClick={restore}
                  className="flex-1 min-w-[200px] bg-[#2E5A36] hover:bg-[#1B2A22] text-white rounded-full font-heading font-bold h-11"
                >
                  ✓ {selected.size} Ürünü Geri Al (Web'e Ekle)
                </Button>
                {mode === "pending" && (
                  <Button
                    data-testid="blacklist-dismiss-btn"
                    onClick={dismiss}
                    variant="outline"
                    className="flex-1 min-w-[200px] border-[#1B2A22] text-[#1B2A22]/80 rounded-full font-heading font-bold h-11"
                  >
                    🚫 {selected.size} Ürünü Yoksay (Silinmiş Kalsın)
                  </Button>
                )}
              </div>
            )}
            <div className="bg-white border border-[#E8E2D5] rounded-xl overflow-hidden mt-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleAllVisible}
                        className="w-4 h-4 accent-[#2E5A36]"
                        data-testid="blacklist-check-all"
                      />
                    </TableHead>
                    <TableHead>Akınsoft Kodu</TableHead>
                    <TableHead>Ürün Adı</TableHead>
                    <TableHead>Silinme Tarihi</TableHead>
                    <TableHead>Son Sync'te Geldi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((it) => {
                    const isPending = !!it.pending_restore;
                    return (
                      <TableRow
                        key={it.akinsoft_code}
                        data-testid={`blacklist-row-${it.akinsoft_code}`}
                        className={isPending ? "bg-[#D9653B]/5" : ""}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selected.has(it.akinsoft_code)}
                            onChange={() => toggle(it.akinsoft_code)}
                            className="w-4 h-4 accent-[#2E5A36]"
                            data-testid={`blacklist-check-${it.akinsoft_code}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs text-[#2E5A36] font-bold">
                          {it.akinsoft_code}
                          {isPending && (
                            <span className="ml-2 inline-flex items-center bg-[#D9653B] text-white rounded-full text-[10px] px-2 py-0.5 font-heading">
                              ⚠️ TEKRAR GELDİ
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{it.name_tr || "—"}</TableCell>
                        <TableCell className="font-mono text-[10px] text-[#1B2A22]/60">
                          {it.deleted_at ? new Date(it.deleted_at).toLocaleString("tr-TR") : "—"}
                        </TableCell>
                        <TableCell className="font-mono text-[10px] text-[#B4522F]">
                          {it.last_seen_in_sync_at
                            ? new Date(it.last_seen_in_sync_at).toLocaleString("tr-TR")
                            : "—"}
                          {it.seen_count ? (
                            <span className="ml-1 text-[#1B2A22]/60">({it.seen_count}x)</span>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
